/**
 * Translation Hook for Tech Portal
 * Provides Spanish translation for technicians with preferred_language = 'es'
 * Features:
 * - LocalStorage caching for static content
 * - Batch translation to reduce API calls
 * - Reverse translation (Spanish input → English) before saving
 */

import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CACHE_KEY = "fsp_translations_cache";
const CACHE_VERSION = "v2";

// Comprehensive static UI text translations (English → Spanish)
const STATIC_TEXTS = {
  // =====================
  // DASHBOARD
  // =====================
  "Today": "Hoy",
  "Today's Schedule": "Horario de Hoy",
  "Today's Visits": "Visitas de Hoy",
  "Completed": "Completado",
  "Upcoming": "Próximo",
  "Overdue": "Atrasado",
  "Overdue Visits": "Visitas Atrasadas",
  "No visits today": "No hay visitas hoy",
  "You have no visits scheduled for today.": "No tienes visitas programadas para hoy.",
  "Upcoming (Next 7 Days)": "Próximos (7 Días)",
  "View All": "Ver Todo",
  "Refresh": "Actualizar",
  
  // =====================
  // VISITS LIST
  // =====================
  "My Visits": "Mis Visitas",
  "visit(s) found": "visita(s) encontrada(s)",
  "visits": "visitas",
  "visit": "visita",
  "Search jobs, customers...": "Buscar trabajos, clientes...",
  "All Dates": "Todas las Fechas",
  "This Week": "Esta Semana",
  "All Statuses": "Todos los Estados",
  "No visits found": "No se encontraron visitas",
  "Try adjusting your filters": "Intenta ajustar tus filtros",
  "You have no assigned visits": "No tienes visitas asignadas",
  "Clear Filters": "Limpiar Filtros",
  "Clear all": "Limpiar todo",
  "Filters": "Filtros",
  "Date Range": "Rango de Fechas",
  "Sort By": "Ordenar Por",
  "Scheduled Time": "Hora Programada",
  "Created Date": "Fecha de Creación",
  "Sort Order": "Orden",
  "Ascending (Earliest First)": "Ascendente (Más Temprano Primero)",
  "Descending (Latest First)": "Descendente (Más Reciente Primero)",
  "Apply Filters": "Aplicar Filtros",
  "Clear All": "Limpiar Todo",
  
  // =====================
  // STATUS LABELS (used in badges and filters)
  // =====================
  "Status": "Estado",
  "Scheduled": "Programado",
  "scheduled": "programado",
  "En Route": "En Camino",
  "en_route": "en camino",
  "On Site": "En el Sitio",
  "on_site": "en el sitio",
  "In Progress": "En Progreso",
  "in_progress": "en progreso",
  "completed": "completado",
  "Canceled": "Cancelado",
  "canceled": "cancelado",
  "Cancelled": "Cancelado",
  "cancelled": "cancelado",
  
  // =====================
  // PRIORITY LABELS
  // =====================
  "Priority": "Prioridad",
  "All Priorities": "Todas las Prioridades",
  "Low": "Baja",
  "low": "baja",
  "Normal": "Normal",
  "normal": "normal",
  "High": "Alta",
  "high": "alta",
  "Urgent": "Urgente",
  "urgent": "urgente",
  
  // =====================
  // SERVICE TYPES / JOB CATEGORIES
  // =====================
  "Service Type": "Tipo de Servicio",
  "All Types": "Todos los Tipos",
  "General": "General",
  "general": "general",
  "Plumbing": "Plomería",
  "plumbing": "plomería",
  "Electrical": "Eléctrico",
  "electrical": "eléctrico",
  "HVAC": "Climatización",
  "hvac": "climatización",
  "Hvac": "Climatización",
  "Maintenance": "Mantenimiento",
  "maintenance": "mantenimiento",
  "Repair": "Reparación",
  "repair": "reparación",
  "Installation": "Instalación",
  "installation": "instalación",
  "Inspection": "Inspección",
  "inspection": "inspección",
  "Emergency": "Emergencia",
  "emergency": "emergencia",
  "Cleaning": "Limpieza",
  "cleaning": "limpieza",
  "Other": "Otro",
  "other": "otro",
  
  // =====================
  // ADDITIONAL STATIC TEXT
  // =====================
  "No address": "Sin dirección",
  "Untitled Job": "Trabajo Sin Título",
  "note(s)": "nota(s)",
  "photo(s)": "foto(s)",
  "Failed to update status": "Error al actualizar estado",
  "Status update queued for sync": "Actualización de estado en cola para sincronizar",
  "Select the current status for this visit": "Selecciona el estado actual de esta visita",
  "Current": "Actual",
  "Updating...": "Actualizando...",
  "I'm on my way to the job": "Estoy en camino al trabajo",
  "I've arrived at the location": "He llegado a la ubicación",
  "Work has started": "El trabajo ha comenzado",
  "Job is done": "Trabajo completado",
  "Failed to add note": "Error al agregar nota",
  "Note queued for sync": "Nota en cola para sincronizar",
  "Your note will be automatically translated to English when saved.": "Tu nota será traducida automáticamente al inglés al guardar.",
  "Translating...": "Traduciendo...",
  "Saving...": "Guardando...",
  "Take Photo or Choose File": "Tomar Foto o Elegir Archivo",
  "Add a description...": "Agregar descripción...",
  "Failed to upload photo": "Error al subir foto",
  "Failed to upload. Check your connection.": "Error al subir. Verifica tu conexión.",
  "Uploading...": "Subiendo...",
  "Confirm that all work has been completed for this visit.": "Confirma que todo el trabajo ha sido completado para esta visita.",
  "This Visit": "Esta Visita",
  "Completing...": "Completando...",
  "Failed to complete visit": "Error al completar visita",
  "Completion queued for sync": "Finalización en cola para sincronizar",
  
  // =====================
  // VISIT DETAIL PAGE
  // =====================
  "Back": "Atrás",
  "Customer & Location": "Cliente y Ubicación",
  "No customer": "Sin cliente",
  "Call": "Llamar",
  "Text": "Mensaje",
  "Map": "Mapa",
  "Open in Maps": "Abrir en Mapas",
  "Address copied": "Dirección copiada",
  "Assigned Technicians": "Técnicos Asignados",
  "Notes": "Notas",
  "Photos": "Fotos",
  "Add Note": "Agregar Nota",
  "Add Photo": "Agregar Foto",
  "Previous Visits": "Visitas Anteriores",
  "Visit Completed": "Visita Completada",
  "Start En Route": "Iniciar En Camino",
  "Complete Visit": "Completar Visita",
  "Update Status": "Actualizar Estado",
  "I'm On My Way": "Estoy en Camino",
  "I've Arrived": "He Llegado",
  "Arrived on site!": "¡Llegué al sitio!",
  "Job Details": "Detalles del Trabajo",
  "Description": "Descripción",
  "No description": "Sin descripción",
  "Job": "Trabajo",
  "Visit": "Visita",
  "Customer": "Cliente",
  "Location": "Ubicación",
  "Address": "Dirección",
  "Phone": "Teléfono",
  "Email": "Correo",
  
  // =====================
  // MODALS & DIALOGS
  // =====================
  "Update Visit Status": "Actualizar Estado de Visita",
  "Choose a new status for this visit": "Elige un nuevo estado para esta visita",
  "Save": "Guardar",
  "Cancel": "Cancelar",
  "Close": "Cerrar",
  "Confirm": "Confirmar",
  "Save Note": "Guardar Nota",
  "Add a Note": "Agregar Nota",
  "Note text": "Texto de la nota",
  "Write your note here...": "Escribe tu nota aquí...",
  "Enter your note...": "Escribe tu nota...",
  "Upload Photo": "Subir Foto",
  "Add a Photo": "Agregar Foto",
  "Select photo": "Seleccionar foto",
  "Caption (optional)": "Descripción (opcional)",
  "Describe this photo...": "Describe esta foto...",
  "Complete This Visit": "Completar Esta Visita",
  "Mark this visit as complete?": "¿Marcar esta visita como completada?",
  "Completion notes (optional)": "Notas de finalización (opcional)",
  "Any final notes...": "Notas finales...",
  "Mark Complete": "Marcar Completo",
  "Note": "Nota",
  "Visibility": "Visibilidad",
  "Internal Only": "Solo Interno",
  "Visible to Customer": "Visible para el Cliente",
  "Only visible to your team": "Solo visible para tu equipo",
  "Customer will see this note": "El cliente verá esta nota",
  
  // =====================
  // NAVIGATION
  // =====================
  "Dashboard": "Panel",
  "Visits": "Visitas",
  "Profile": "Perfil",
  "Settings": "Configuración",
  "Logout": "Cerrar Sesión",
  "Menu": "Menú",
  
  // =====================
  // LOADING & ERROR STATES
  // =====================
  "Loading...": "Cargando...",
  "Loading": "Cargando",
  "Loading your dashboard...": "Cargando tu panel...",
  "Loading visit details...": "Cargando detalles de la visita...",
  "Loading visits...": "Cargando visitas...",
  "Failed to load dashboard": "Error al cargar el panel",
  "Failed to load visit": "Error al cargar la visita",
  "Failed to load visits": "Error al cargar las visitas",
  "Visit not found": "Visita no encontrada",
  "Go Back": "Volver",
  "Error": "Error",
  "Something went wrong": "Algo salió mal",
  "Please try again": "Por favor intenta de nuevo",
  "Retry": "Reintentar",
  
  // =====================
  // SUCCESS MESSAGES
  // =====================
  "Dashboard refreshed": "Panel actualizado",
  "Visit completed!": "¡Visita completada!",
  "Status updated": "Estado actualizado",
  "Note added": "Nota agregada",
  "Photo uploaded": "Foto subida",
  "Changes saved": "Cambios guardados",
  "Success": "Éxito",
  
  // =====================
  // ACTION BUTTONS
  // =====================
  "Submit": "Enviar",
  "Delete": "Eliminar",
  "Edit": "Editar",
  "View": "Ver",
  "Download": "Descargar",
  "Share": "Compartir",
  "Copy": "Copiar",
  "Print": "Imprimir",
  "Search": "Buscar",
  "Filter": "Filtrar",
  "Sort": "Ordenar",
  "Reset": "Restablecer",
  "Apply": "Aplicar",
  "Continue": "Continuar",
  "Next": "Siguiente",
  "Previous": "Anterior",
  "Done": "Hecho",
  "OK": "OK",
  "Yes": "Sí",
  "No": "No",
  
  // =====================
  // TIME & DATE
  // =====================
  "Today": "Hoy",
  "Tomorrow": "Mañana",
  "Yesterday": "Ayer",
  "This week": "Esta semana",
  "Next week": "Próxima semana",
  "Last week": "Semana pasada",
  "AM": "AM",
  "PM": "PM",
  "hour": "hora",
  "hours": "horas",
  "minute": "minuto",
  "minutes": "minutos",
  "day": "día",
  "days": "días",
  "week": "semana",
  "weeks": "semanas",
  "month": "mes",
  "months": "meses",
  "ago": "hace",
  "from now": "desde ahora",
  
  // =====================
  // MISCELLANEOUS
  // =====================
  "photo(s)": "foto(s)",
  "1 photo(s)": "1 foto",
  "Online": "En Línea",
  "Offline": "Sin Conexión",
  "Syncing...": "Sincronizando...",
  "Synced": "Sincronizado",
  "Pending": "Pendiente",
  "N/A": "N/D",
  "None": "Ninguno",
  "All": "Todos",
  "Select": "Seleccionar",
  "Select all": "Seleccionar todo",
  "Deselect all": "Deseleccionar todo",
  "No results": "Sin resultados",
  "No data": "Sin datos",
  "Empty": "Vacío",
  "Unknown": "Desconocido",
  "Not available": "No disponible",
  "Optional": "Opcional",
  "Required": "Requerido",
  "more": "más",
  "less": "menos",
  "and": "y",
  "or": "o",
  "of": "de",
  "to": "a",
  "from": "de",
  "at": "a las",
  "on": "el",
  "in": "en",
  "for": "para",
  "with": "con",
  "by": "por",
  
  // =====================
  // COMMON JOB TITLES (for faster translation)
  // =====================
  "Thermostat Replacement": "Reemplazo de Termostato",
  "HVAC System Inspection": "Inspección del Sistema de Climatización",
  "Water Heater Repair": "Reparación de Calentador de Agua",
  "Pipe Repair": "Reparación de Tubería",
  "Drain Cleaning": "Limpieza de Drenaje",
  "AC Repair": "Reparación de Aire Acondicionado",
  "Furnace Maintenance": "Mantenimiento de Calefacción",
  "Toilet Installation": "Instalación de Inodoro",
  "Faucet Replacement": "Reemplazo de Grifo",
  "Electrical Panel Upgrade": "Actualización de Panel Eléctrico",
  "Light Fixture Installation": "Instalación de Luminaria",
  "Outlet Repair": "Reparación de Tomacorriente",
  "Garbage Disposal Repair": "Reparación de Triturador",
  "Sump Pump Installation": "Instalación de Bomba de Sumidero",
  "Duct Cleaning": "Limpieza de Conductos",
  "Filter Replacement": "Reemplazo de Filtro",
  "Leak Detection": "Detección de Fugas",
  "Water Line Repair": "Reparación de Línea de Agua",
  "Gas Line Inspection": "Inspección de Línea de Gas",
  "Smoke Detector Installation": "Instalación de Detector de Humo",
};

// Create context for translation
const TranslationContext = createContext(null);

/**
 * Translation Provider - wraps the tech portal
 * 
 * Uses backend gate to determine if translation should occur.
 * Translation only happens when:
 * 1) Tenant has spanish_translation entitlement
 * 2) Technician.preferred_language == "es"
 * 3) Context is tech_ui (enforced by this provider)
 */
export function TranslationProvider({ children, technician, translationEntitled }) {
  const [cache, setCache] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [backendGateResult, setBackendGateResult] = useState(null);
  const pendingTextsRef = useRef(new Set());
  const batchTimeout = useRef(null);
  const isFetching = useRef(false);

  // Determine if translation should be active based on BACKEND gate
  // This is the ONLY source of truth for translation decision
  const shouldTranslate = backendGateResult?.shouldTranslate === true;
  
  // Context type for this provider - always tech_ui for tech portal
  const contextType = "tech_ui";

  // Fetch backend gate result on mount or when technician changes
  useEffect(() => {
    const fetchGateResult = async () => {
      // Quick frontend check - don't call backend if tech is English
      if (!technician || technician.preferred_language !== "es") {
        setBackendGateResult({ shouldTranslate: false, reason: "tech_language_not_es" });
        return;
      }
      
      // Quick frontend check - don't call backend if not entitled
      if (!translationEntitled) {
        setBackendGateResult({ shouldTranslate: false, reason: "tenant_not_entitled" });
        return;
      }
      
      // Check sessionStorage cache first
      const cacheKey = `translation_gate_${technician?.technician_id || "unknown"}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          // Cache valid for 5 minutes
          if (parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
            setBackendGateResult(parsed);
            return;
          }
        } catch (e) {
          // Ignore cache parse errors
        }
      }
      
      // Fetch from backend
      try {
        const response = await fetch(`${API}/translate/status`, {
          credentials: "include",
        });
        
        if (response.ok) {
          const data = await response.json();
          const result = {
            shouldTranslate: data.should_translate,
            entitled: data.entitled,
            technicianLanguage: data.technician_language,
            reason: data.reason,
            timestamp: Date.now()
          };
          setBackendGateResult(result);
          
          // Cache in sessionStorage
          sessionStorage.setItem(cacheKey, JSON.stringify(result));
        } else {
          setBackendGateResult({ shouldTranslate: false, reason: "api_error" });
        }
      } catch (error) {
        console.error("Failed to fetch translation gate:", error);
        setBackendGateResult({ shouldTranslate: false, reason: "network_error" });
      }
    };
    
    fetchGateResult();
  }, [technician, translationEntitled]);

  // Load cache from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.version === CACHE_VERSION) {
          setCache(parsed.translations || {});
        } else {
          // Clear old version cache
          localStorage.removeItem(CACHE_KEY);
        }
      }
    } catch (e) {
      console.warn("Failed to load translation cache:", e);
    }
  }, []);

  // Save cache to localStorage when it changes
  useEffect(() => {
    if (Object.keys(cache).length > 0) {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          version: CACHE_VERSION,
          translations: cache,
          updated: new Date().toISOString()
        }));
      } catch (e) {
        console.warn("Failed to save translation cache:", e);
      }
    }
  }, [cache]);

  /**
   * Process batch translation for queued texts
   * Always sends context_type="tech_ui" to enforce gate on backend
   */
  const processBatchTranslation = useCallback(async (textsToTranslate) => {
    // Safety check: Don't call if gate says no
    if (!shouldTranslate) {
      return;
    }
    
    if (textsToTranslate.length === 0 || isFetching.current) return;
    
    isFetching.current = true;
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API}/translate/batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          texts: textsToTranslate,
          context_type: contextType  // Always tech_ui for tech portal
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.translations) {
          // Convert array to object for cache
          const translationMap = {};
          data.translations.forEach(t => {
            if (t.original && t.translated) {
              translationMap[t.original] = t.translated;
            }
          });
          setCache(prev => ({ ...prev, ...translationMap }));
        }
      }
    } catch (error) {
      console.error("Batch translation failed:", error);
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, [shouldTranslate, contextType]);

  /**
   * Translate a single text string
   * Uses static cache first, then localStorage cache, then queues for API
   */
  const t = useCallback((text) => {
    if (!shouldTranslate || !text || typeof text !== 'string') return text;
    
    const trimmedText = text.trim();
    if (!trimmedText) return text;
    
    // Check static translations first (instant)
    if (STATIC_TEXTS[trimmedText]) {
      return STATIC_TEXTS[trimmedText];
    }
    
    // Check dynamic cache
    if (cache[trimmedText]) {
      return cache[trimmedText];
    }
    
    // Queue for batch translation using ref (avoid setState during render)
    if (!pendingTextsRef.current.has(trimmedText)) {
      pendingTextsRef.current.add(trimmedText);
      
      // Debounce batch translation
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      batchTimeout.current = setTimeout(() => {
        const textsArray = Array.from(pendingTextsRef.current).filter(t => !STATIC_TEXTS[t] && !cache[t]);
        pendingTextsRef.current.clear();
        if (textsArray.length > 0) {
          processBatchTranslation(textsArray);
        }
      }, 500);
    }
    
    // Return original text while translation is pending
    return text;
  }, [shouldTranslate, cache, processBatchTranslation]);

  /**
   * Translate Spanish input to English before saving
   */
  const translateToEnglish = useCallback(async (text) => {
    if (!shouldTranslate || !text?.trim()) return text;
    
    try {
      const response = await fetch(`${API}/translate/to-english`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text }),
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.translated || text;
      }
    } catch (error) {
      console.error("Translation to English failed:", error);
    }
    
    return text;
  }, [shouldTranslate]);

  /**
   * Preload translations for a set of texts (call on page load)
   */
  const preloadTranslations = useCallback(async (texts) => {
    if (!shouldTranslate || !texts || texts.length === 0) return;
    
    const uncached = texts.filter(text => 
      text && typeof text === 'string' && !STATIC_TEXTS[text.trim()] && !cache[text.trim()]
    );
    
    if (uncached.length === 0) return;
    
    await processBatchTranslation(uncached);
  }, [shouldTranslate, cache, processBatchTranslation]);

  /**
   * Translate status codes to display labels
   */
  const translateStatus = useCallback((status) => {
    if (!shouldTranslate) {
      const statusLabels = {
        scheduled: "Scheduled",
        en_route: "En Route",
        on_site: "On Site",
        in_progress: "In Progress",
        completed: "Completed",
        canceled: "Canceled",
        cancelled: "Cancelled",
      };
      return statusLabels[status] || status;
    }
    
    const statusLabels = {
      scheduled: "Programado",
      en_route: "En Camino",
      on_site: "En el Sitio",
      in_progress: "En Progreso",
      completed: "Completado",
      canceled: "Cancelado",
      cancelled: "Cancelado",
    };
    return statusLabels[status] || status;
  }, [shouldTranslate]);

  /**
   * Translate priority codes to display labels
   */
  const translatePriority = useCallback((priority) => {
    if (!shouldTranslate) {
      const priorityLabels = {
        low: "Low",
        normal: "Normal",
        high: "High",
        urgent: "Urgent",
      };
      return priorityLabels[priority] || priority;
    }
    
    const priorityLabels = {
      low: "Baja",
      normal: "Normal",
      high: "Alta",
      urgent: "Urgente",
    };
    return priorityLabels[priority] || priority;
  }, [shouldTranslate]);

  /**
   * Translate service type/category
   */
  const translateServiceType = useCallback((type) => {
    if (!shouldTranslate) return type;
    
    const typeLabels = {
      general: "General",
      plumbing: "Plomería",
      electrical: "Eléctrico",
      hvac: "Climatización",
      maintenance: "Mantenimiento",
      repair: "Reparación",
      installation: "Instalación",
      inspection: "Inspección",
      emergency: "Emergencia",
      cleaning: "Limpieza",
      other: "Otro",
    };
    return typeLabels[type?.toLowerCase()] || t(type) || type;
  }, [shouldTranslate, t]);

  const value = {
    t,
    translateToEnglish,
    preloadTranslations,
    translateStatus,
    translatePriority,
    translateServiceType,
    shouldTranslate,
    isLoading,
    cache,
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

/**
 * Hook to use translation in components
 */
export function useTranslation() {
  const context = useContext(TranslationContext);
  
  if (!context) {
    // Return a no-op version if not in provider (fallback to English)
    return {
      t: (text) => text,
      translateToEnglish: async (text) => text,
      preloadTranslations: async () => {},
      translateStatus: (status) => {
        const labels = { scheduled: "Scheduled", en_route: "En Route", on_site: "On Site", in_progress: "In Progress", completed: "Completed", canceled: "Canceled" };
        return labels[status] || status;
      },
      translatePriority: (priority) => {
        const labels = { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" };
        return labels[priority] || priority;
      },
      translateServiceType: (type) => type,
      shouldTranslate: false,
      isLoading: false,
      cache: {},
    };
  }
  
  return context;
}

/**
 * Hook to check translation status on mount
 * Uses centralized gate that checks:
 * 1) Tenant entitlement
 * 2) Technician language
 * 3) Context type
 */
export function useTranslationStatus() {
  const [status, setStatus] = useState({
    loading: true,
    entitled: false,
    shouldTranslate: false,
    technicianLanguage: "en",
    reason: "",
    error: null,
  });

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API}/translate/status`, {
          credentials: "include",
        });
        
        if (response.ok) {
          const data = await response.json();
          setStatus({
            loading: false,
            entitled: data.entitled,
            shouldTranslate: data.should_translate,
            technicianLanguage: data.technician_language || "en",
            reason: data.reason || "",
            error: null,
          });
        } else {
          setStatus({
            loading: false,
            entitled: false,
            shouldTranslate: false,
            technicianLanguage: "en",
            reason: "api_error",
            error: "Failed to check translation status",
          });
        }
      } catch (error) {
        setStatus({
          loading: false,
          entitled: false,
          shouldTranslate: false,
          technicianLanguage: "en",
          reason: "network_error",
          error: error.message,
        });
      }
    };
    
    checkStatus();
  }, []);

  return status;
}

export default useTranslation;
